import classNames from 'classnames';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import VoiceTrackIcon from '../../../static/icons/ic_voicetrack_16.svg';
import * as Actions from '../../actions';
import RowItem from '../../components/lib/RowItem/RowItem';
import { messages } from '../../constants/Collection';
import { GridConstants } from '../../constants/Grid';
import { PandoraTypes } from '../../constants/SourceTypes';
import { getInDomain } from '../../domains/externalUtils';
import { getArt } from '../../lib/ArtUtils';
import { AppContextIntegrationData } from '../../lib/Context';
import { getFromMap } from '../../lib/CoreUtils';
import { mapCuratorProps } from '../../lib/CuratorSourcesUtils';
import {
  getProfilePath,
  getStationDetailsPathForStationId,
  getStationPlayPathForStationCode,
} from '../../lib/RouteUtils';
import { getSourceCardMenuItems } from '../../lib/SourceCardUtils';
import Icon from '../Icon';

import StationRowItemRightColChild from './StationRowItemRightColChild';

const voiceTrackIcon = <Icon glyph={VoiceTrackIcon} />;

export function stationIdentifierGenerator(targetId) {
  return 'MyStationsListItem__' + targetId;
}

/*
 * StationRowItem is a more-or-less direct conversion of MyStationsRowItem into
 * the new T3 row item, for use in My Music.
 */
class StationRowItem extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    shouldHideDate: PropTypes.bool,
    hasBorderTopBottom: PropTypes.bool,
    stationName: PropTypes.string,
    imgUrl: PropTypes.string,
    dominantColor: PropTypes.string,
    isPlayingCurrently: PropTypes.bool,
    isPlaying: PropTypes.bool,
    shouldResumePlayback: PropTypes.bool,
    isShuffleActive: PropTypes.bool,
    isPaused: PropTypes.bool,
    totalPlayTime: PropTypes.number,
    sourceCardMenuItems: PropTypes.array,
    nowPlayingUrl: PropTypes.string,
    totalStations: PropTypes.number,
    position: PropTypes.number,
    isAdvertiserStation: PropTypes.bool,
    sortBy: PropTypes.string,
    editStationUrl: PropTypes.string,
    isShared: PropTypes.bool,
    createdBy: PropTypes.string,
    stationCreated: PropTypes.string,
    canShuffle: PropTypes.bool,
    isInShuffle: PropTypes.bool,
    pauseTrack: PropTypes.func.isRequired,
    playResumeSource: PropTypes.func.isRequired,
    startStation: PropTypes.func.isRequired,
    addStationToShuffleList: PropTypes.func.isRequired,
    removeStationFromShuffleList: PropTypes.func.isRequired,
    logWebViewModeEvent: PropTypes.func.isRequired,
    intl: PropTypes.object,
    showLastPlayed: PropTypes.bool,
    lastPlayed: PropTypes.string,
    isHosted: PropTypes.bool,
    componentName: PropTypes.string,
    curatorName: PropTypes.string,
    curatorUrlPath: PropTypes.string,
  };

  static contextTypes = {
    router: PropTypes.object,
  };

  state = {
    shuffleIsOn: false,
  };

  startOrResumeStation = () => {
    if (this.props.shouldResumePlayback) {
      this.props.playResumeSource({ id: this.props.id });
    } else {
      this.props.startStation({
        componentName: 'MyStationsListItem',
        stationListLength: this.props.totalStations,
        stationPosition: this.props.position,
        rerouteBasedOnTier: true,
        stationId: this.props.id,
      });
    }
  };

  shuffleOnclickIfDisabled = () => {
    this.props.addStationToShuffleList(this.props.id);
  };

  shuffleOnclick = () => {
    this.props.removeStationFromShuffleList(this.props.id);
  };

  getIntegrationData = () => {
    return {
      id: this.props.id,
    };
  };

  getSecondTextProps = () => {
    let secondText = this.props.intl.formatMessage(messages.station);
    let secondTextModifier = '';
    let secondTextUrl = null;

    if (this.props.isAdvertiserStation) {
      secondText = this.props.intl.formatMessage(messages.stationByPandora);
    } else if (this.props.isShared && this.props.createdBy) {
      secondTextModifier = this.props.intl.formatMessage(messages.stationBy);
      secondText = this.props.createdBy;
      secondTextUrl = getProfilePath(this.props.createdBy);
    } else if (this.props.curatorName && this.props.curatorUrlPath) {
      secondTextModifier = this.props.intl.formatMessage(messages.stationBy);
      secondText = this.props.curatorName;
      secondTextUrl = this.props.curatorUrlPath;
    }

    return {
      secondTextModifier,
      secondText,
      secondTextUrl,
    };
  };

  render() {
    const mainTextModifier = this.props.isHosted ? voiceTrackIcon : null;
    const { secondText, secondTextModifier, secondTextUrl } = this.getSecondTextProps();

    return (
      <AppContextIntegrationData data={this.getIntegrationData}>
        <div className={classNames('StationRowItem', stationIdentifierGenerator(this.props.id))}>
          <RowItem
            imgUrl={this.props.imgUrl}
            dominantColor={this.props.dominantColor}
            mainText={this.props.stationName}
            mainTextUrl={
              !this.props.isAdvertiserStation ? this.props.editStationUrl : this.props.nowPlayingUrl
            }
            mainTextModifier={mainTextModifier}
            secondText={secondText}
            secondTextModifier={secondTextModifier}
            secondTextUrl={secondTextUrl}
            playButtonOnclick={this.startOrResumeStation}
            pauseButtonOnclick={this.props.pauseTrack}
            isPlaying={this.props.isPlaying && !this.props.isPaused}
            isPaused={this.props.isPaused}
            isStation={!this.props.isAdvertiserStation}
            isShuffleActive={this.props.isShuffleActive}
            canShuffle={this.props.canShuffle}
            isShuffleDisabled={this.props.canShuffle && !this.props.isInShuffle}
            shuffleOnclick={this.shuffleOnclick}
            shuffleOnclickIfDisabled={this.shuffleOnclickIfDisabled}
            sourceCardMenuItems={this.props.sourceCardMenuItems}
            onMouseEnter={this.onMouseEnter}
            isHosted={this.props.isHosted}
            hasBorderTopBottom={this.props.hasBorderTopBottom}
            rightColChild={
              <StationRowItemRightColChild
                stationCreated={this.props.stationCreated}
                shouldHideDate={this.props.shouldHideDate}
                sortBy={this.props.sortBy}
                totalPlayTime={this.props.totalPlayTime}
              />
            }
          />
        </div>
      </AppContextIntegrationData>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const stationId = ownProps.id;
  const station =
    ownProps.details || getInDomain('playback.utils.getStationByStationId')(state, stationId);
  const lastPlayed = ownProps.showLastPlayed
    ? getInDomain('playback.utils.getStationLastPlayed')(station)
    : null;
  const isPlayingCurrently = stationId === getInDomain('playback.utils.getCurrentStationId')(state);
  const isShuffleActive = getInDomain('playback.utils.isInShuffleMode')(state);
  const isInShuffle =
    isShuffleActive && getInDomain('playback.utils.isStationIdInShuffle')(state, stationId);
  const canShuffle =
    ownProps.canShuffle &&
    isShuffleActive &&
    getInDomain('playback.utils.isAvailableToShuffle')(state, stationId);
  const editStationUrl = getStationDetailsPathForStationId(stationId);
  const isAdvertiserStation = getInDomain('playback.utils.getStationIsAdvertiser')(
    state,
    stationId,
  );
  const { curatorName, curatorUrlPath } = mapCuratorProps(
    state,
    getFromMap(station, 'stationFactoryPandoraId'),
  );

  const isActiveShuffle = getInDomain('playback.utils.isStationShuffling')(state, stationId);
  const isAudioPlaying = getInDomain('playback.utils.isAudioPlaying')(state);
  const stationCreatedDate =
    station && getInDomain('playback.utils.getStationCreatedDate')(station);
  const stationCreated = stationCreatedDate || null;
  const isHosted = getInDomain('playback.utils.isHostedStation')(state, stationId);
  const sourceCardMenuItems = getSourceCardMenuItems({
    state,
    pandoraId: stationId,
    pandoraType: PandoraTypes.STATION,
    shareParentSource: {
      shareParentSourceId: stationId,
      shareParentSourceType: PandoraTypes.STATION,
    },
    stationId,
    componentName: ownProps.componentName,
  });

  return {
    stationName: getFromMap(station, 'name') || '',
    isShared: getFromMap(station, 'isShared'),
    totalPlayTime: getFromMap(station, 'totalPlayTime') || 0,
    // TODO: designs have user's first and last name on shared stations, not their webname
    createdBy: getFromMap(station, 'creatorWebname'),
    imgUrl: getArt(station, GridConstants.GRID_ITEM_IMAGE_SIZE, state),
    dominantColor: getFromMap(station, 'dominantColor'),
    nowPlayingUrl: getStationPlayPathForStationCode(stationId),
    isPlaying: isPlayingCurrently && isAudioPlaying,
    isPaused: !isAudioPlaying && isPlayingCurrently,
    shouldResumePlayback:
      isPlayingCurrently && getInDomain('playback.utils.getCurrentTrackTime')(state) > 0,
    editStationUrl,
    isPlayingCurrently,
    lastPlayed,
    isInShuffle,
    isShuffleActive,
    canShuffle,
    isAdvertiserStation,
    isActiveShuffle,
    sourceCardMenuItems,
    stationCreated,
    isHosted,
    curatorName,
    curatorUrlPath,
  };
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      pauseTrack: Actions.UseCases.pauseTrack,
      playResumeSource: Actions.UseCases.playResumeSource,
      startStation: Actions.UseCases.startStation,
      addStationToShuffleList: Actions.Playback.addStationToShuffleList,
      removeStationFromShuffleList: Actions.Playback.removeStationFromShuffleList,
      logWebViewModeEvent: getInDomain('analytics.actions.logWebViewModeEvent', noop),
    },
    dispatch,
  );
};

// We use injectIntl here because there was some inexplicable performance issues when using <FormattedMessage> directly in the props
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(StationRowItem));
